// Internal module — import from `../CommunicationStyleService` instead.
//
// Style application logic — extracted from CommunicationStyleService in
// Sprint 4 #54 to keep the facade under the 800-LOC budget.
//
// Pure functions only: each takes its inputs explicitly so they're trivially
// testable without instantiating the service.

/**
 * Generate placeholders based on communication style
 * @param {Object} style - Communication style
 * @returns {Array} Array of placeholder suggestions
 */
export function generateStylePlaceholders(style) {
  const placeholders = [];

  if (style.firmName) {
    placeholders.push('{{company_name}}');
  }

  if (style.personalTouch === 'high') {
    placeholders.push('{{client_first_name}}', '{{personal_note}}');
  }

  if (style.technicalLevel === 'advanced') {
    placeholders.push('{{technical_analysis}}', '{{market_metrics}}');
  } else if (style.technicalLevel === 'basic') {
    placeholders.push('{{simple_explanation}}', '{{plain_language_summary}}');
  }

  if (style.confidence === 'confident') {
    placeholders.push('{{strong_recommendation}}', '{{definitive_outlook}}');
  } else if (style.confidence === 'cautious') {
    placeholders.push('{{measured_advice}}', '{{balanced_perspective}}');
  }

  return placeholders;
}

/**
 * Apply communication style to content with enhanced transformations
 * @param {string} content - Original content
 * @param {Object} style - Communication style object
 * @param {Object} options - Optional settings { returnMetadata: boolean }
 * @returns {string|Object} Styled content or { content, transformations } if returnMetadata=true
 */
export function applyStyleToContent(content, style, options = {}) {
  if (!style || !content) return options.returnMetadata ? { content, transformations: [] } : content;

  let styledContent = content;
  const transformations = [];

  // Helper to track transformations
  const trackReplace = (pattern, replacement, type) => {
    const originalContent = styledContent;
    styledContent = styledContent.replace(pattern, (...args) => {
      const match = args[0];
      transformations.push({ from: match, to: replacement, type });
      return typeof replacement === 'function' ? replacement(...args) : replacement;
    });
    return styledContent !== originalContent;
  };

  // ===========================================
  // FORMALITY TRANSFORMATIONS (Casual)
  // ===========================================
  if (style.formality === 'casual') {
    // Greetings and closings
    trackReplace(/\bDear Valued Client\b/g, `Hey ${style.signatureGreeting || 'there'}`, 'formality');
    trackReplace(/\bDear Client\b/g, "Hi there", 'formality');
    trackReplace(/\bBest regards\b/gi, style.signatureClosing || "Thanks", 'formality');
    trackReplace(/\bSincerely\b/gi, "Thanks", 'formality');

    // Formal phrases → Casual
    trackReplace(/\bWe recommend that you\b/gi, "We'd suggest you", 'formality');
    trackReplace(/\bWe recommend\b/gi, "We'd suggest", 'formality');
    trackReplace(/\bIt is important to\b/gi, "It's important to", 'formality');
    trackReplace(/\bIt is essential\b/gi, "It's essential", 'formality');
    trackReplace(/\bDo not hesitate to\b/gi, "Feel free to", 'formality');
    trackReplace(/\bPlease do not hesitate\b/gi, "Please feel free", 'formality');
    trackReplace(/\bYou are encouraged to\b/gi, "You should", 'formality');
    trackReplace(/\bWe encourage you to\b/gi, "We'd love for you to", 'formality');
    trackReplace(/\bIn light of\b/gi, "Given", 'formality');
    trackReplace(/\bWith regard to\b/gi, "About", 'formality');
    trackReplace(/\bRegarding\b/gi, "About", 'formality');
    trackReplace(/\bConcerning\b/gi, "About", 'formality');
    trackReplace(/\bPertaining to\b/gi, "About", 'formality');

    // Conjunctions - context-aware
    trackReplace(/\bFurthermore\b/g, "Also", 'formality');
    trackReplace(/\bMoreover\b/g, "Plus", 'formality');
    trackReplace(/\bAdditionally\b/g, "Also", 'formality');
    trackReplace(/\bNevertheless\b/g, "Still", 'formality');
    trackReplace(/([.!?]\s+)However\b/g, "$1But", 'formality');
    trackReplace(/^However\b/gm, "But", 'formality');

    // Formal verbs → Casual
    trackReplace(/\bpurchase\b/gi, "buy", 'formality');
    trackReplace(/\butilize\b/gi, "use", 'formality');
    trackReplace(/\bcommence\b/gi, "start", 'formality');
    trackReplace(/\bterminate\b/gi, "end", 'formality');
    trackReplace(/\bassist\b/gi, "help", 'formality');

    // Professional titles
    trackReplace(/\bYour Financial Advisor\b/gi, style.firmName || "Your Advisor", 'formality');
  }

  // ===========================================
  // FORMALITY TRANSFORMATIONS (Formal)
  // ===========================================
  else if (style.formality === 'formal') {
    // Greetings and closings
    trackReplace(/\bHey there\b/gi, style.signatureGreeting || "Dear Valued Client", 'formality');
    trackReplace(/\bHi there\b/gi, "Dear Client", 'formality');
    trackReplace(/\bThanks\b(?! to)/gi, "Best regards", 'formality');

    // Casual phrases → Formal
    trackReplace(/\bWe'd suggest you\b/gi, "We recommend that you", 'formality');
    trackReplace(/\bWe'd suggest\b/gi, "We recommend", 'formality');
    trackReplace(/\bIt's important to\b/gi, "It is important to", 'formality');
    trackReplace(/\bIt's essential\b/gi, "It is essential", 'formality');
    trackReplace(/\bFeel free to\b/gi, "Do not hesitate to", 'formality');
    trackReplace(/\bPlease feel free\b/gi, "Please do not hesitate", 'formality');
    trackReplace(/\bYou should\b/gi, "You are encouraged to", 'formality');
    trackReplace(/\bWe'd love for you to\b/gi, "We encourage you to", 'formality');
    trackReplace(/\bGiven\b/gi, "In light of", 'formality');
    trackReplace(/\bAbout\b/gi, "Regarding", 'formality');

    // Conjunctions - context-aware
    trackReplace(/([.!?]\s+)Also\b/g, "$1Furthermore", 'formality');
    trackReplace(/^Also\b/gm, "Furthermore", 'formality');
    trackReplace(/([.!?]\s+)Plus\b/g, "$1Moreover", 'formality');
    trackReplace(/^Plus\b/gm, "Moreover", 'formality');
    trackReplace(/([.!?]\s+)Still\b/g, "$1Nevertheless", 'formality');
    trackReplace(/^Still\b/gm, "Nevertheless", 'formality');
    // CRITICAL: Only replace "But" at sentence start/after punctuation (not in "not... but...")
    trackReplace(/([.!?]\s+)But\b/g, "$1However", 'formality');
    trackReplace(/^But\b/gm, "However", 'formality');

    // Casual verbs → Formal
    trackReplace(/\bbuy\b/gi, "purchase", 'formality');
    trackReplace(/\buse\b/gi, "utilize", 'formality');
    trackReplace(/\bstart\b/gi, "commence", 'formality');
    trackReplace(/\bend\b/gi, "terminate", 'formality');
    trackReplace(/\bhelp\b/gi, "assist", 'formality');
  }

  // ===========================================
  // FORMALITY TRANSFORMATIONS (Balanced) — #174
  // ===========================================
  // Default-fallback signatures (e.g. tone_markers=['professional','informative']
  // with empty common_phrases) used to collapse to formality='balanced' which
  // had NO transform branch — preview rendered identical panels. Apply a
  // small set of safe register-neutralising swaps so the preview always
  // shows *some* movement when a signatureGreeting / signatureClosing is
  // present, and so the user sees the system is doing something.
  else if (style.formality === 'balanced') {
    if (style.signatureGreeting) {
      trackReplace(/\bDear Valued Client\b/g, style.signatureGreeting, 'formality');
      trackReplace(/\bDear Client\b/g, style.signatureGreeting, 'formality');
    }
    if (style.signatureClosing) {
      trackReplace(/\bSincerely\b/g, style.signatureClosing, 'formality');
      trackReplace(/\bBest regards\b/g, style.signatureClosing, 'formality');
    }
    // Light register normalization — nothing dramatic, just enough to
    // demonstrate the engine is engaged.
    trackReplace(/\bIt is important to\b/gi, "It's important to", 'formality');
    trackReplace(/\bDo not hesitate to\b/gi, "Please feel free to", 'formality');
    trackReplace(/\bWith regard to\b/gi, "Regarding", 'formality');
  }

  // ===========================================
  // CONFIDENCE LEVEL ADJUSTMENTS (Confident)
  // ===========================================
  if (style.confidence === 'confident') {
    // Modal verbs - context-aware
    trackReplace(/\bmay\s+(be|have|see|experience|find|need|provide|offer|result|lead)\b/gi,
      (match, verb) => `will ${verb}`, 'confidence');
    trackReplace(/\bcould\s+(be|have|see|experience|lead|result|provide|offer)\b/gi,
      (match, verb) => `should ${verb}`, 'confidence');
    trackReplace(/\bmight\s+(be|have|see|experience|consider)\b/gi,
      (match, verb) => `will likely ${verb}`, 'confidence');

    // Hedging language → Confident language
    trackReplace(/\bwe believe\b/gi, "we are confident", 'confidence');
    trackReplace(/\bwe think\b/gi, "we are confident", 'confidence');
    trackReplace(/\bappears to\b/gi, "demonstrates", 'confidence');
    trackReplace(/\bseems to\b/gi, "indicates", 'confidence');
    trackReplace(/\bsuggests that\b/gi, "indicates that", 'confidence');
    trackReplace(/\btends to\b/gi, "consistently", 'confidence');
    trackReplace(/\blikely\b/gi, "expected", 'confidence');
    trackReplace(/\bpossibly\b/gi, "probably", 'confidence');
    trackReplace(/\bperhaps\b/gi, "likely", 'confidence');
    trackReplace(/\bwe expect\b/gi, "we anticipate", 'confidence');
  }

  // ===========================================
  // CONFIDENCE LEVEL ADJUSTMENTS (Cautious)
  // ===========================================
  else if (style.confidence === 'cautious') {
    // Modal verbs - context-aware
    trackReplace(/\bwill\s+(likely|probably|be|have|see|experience|provide|offer)\b/gi,
      (match, verb) => `may ${verb}`, 'confidence');
    trackReplace(/\bshould\s+(be|have|consider|review|evaluate|provide|offer)\b/gi,
      (match, verb) => `could ${verb}`, 'confidence');
    trackReplace(/\bwill likely\s+(be|have|see|experience)\b/gi,
      (match, verb) => `might ${verb}`, 'confidence');

    // Confident language → Hedging language
    trackReplace(/\bwe are confident\b/gi, "we believe", 'confidence');
    trackReplace(/\bdemonstrates\b/gi, "appears to", 'confidence');
    trackReplace(/\bindicates that\b/gi, "suggests that", 'confidence');
    trackReplace(/\bindicates\b/gi, "seems to", 'confidence');
    trackReplace(/\bconsistently\b/gi, "tends to", 'confidence');
    trackReplace(/\bexpected\b/gi, "likely", 'confidence');
    trackReplace(/\bprobably\b/gi, "possibly", 'confidence');
    trackReplace(/\bwe anticipate\b/gi, "we expect", 'confidence');
  }

  // ===========================================
  // TECHNICAL LEVEL ADJUSTMENTS (Basic/Beginner)
  // ===========================================
  if (style.technicalLevel === 'basic' || style.technicalLevel === 'beginner') {
    // CRITICAL FIX: Preserve capitalization by handling both cases
    // Multi-word phrases first (to avoid partial replacements)
    trackReplace(/\bAsset allocation\b/g, "Investment mix", 'vocabulary');
    trackReplace(/\basset allocation\b/g, "investment mix", 'vocabulary');
    trackReplace(/\bPortfolio allocation\b/g, "How your investments are divided", 'vocabulary');
    trackReplace(/\bportfolio allocation\b/g, "how your investments are divided", 'vocabulary');
    trackReplace(/\bMarket volatility\b/g, "Market ups and downs", 'vocabulary');
    trackReplace(/\bmarket volatility\b/g, "market ups and downs", 'vocabulary');
    trackReplace(/\bFixed-income securities\b/g, "Bonds", 'vocabulary');
    trackReplace(/\bfixed-income securities\b/g, "bonds", 'vocabulary');
    trackReplace(/\bFixed-income\b/g, "Bonds", 'vocabulary');
    trackReplace(/\bfixed-income\b/g, "bonds", 'vocabulary');

    // Single words (after multi-word phrases)
    trackReplace(/\bVolatility\b/g, "Ups and downs", 'vocabulary');
    trackReplace(/\bvolatility\b/g, "ups and downs", 'vocabulary');
    trackReplace(/\bEquities\b/g, "Stocks", 'vocabulary');
    trackReplace(/\bequities\b/g, "stocks", 'vocabulary');
    trackReplace(/\bAppreciation\b/g, "Growth", 'vocabulary');
    trackReplace(/\bappreciation\b/g, "growth", 'vocabulary');
    trackReplace(/\bLiquidity\b/g, "Available cash", 'vocabulary');
    trackReplace(/\bliquidity\b/g, "available cash", 'vocabulary');
    trackReplace(/\bDiversification\b/g, "Spreading investments", 'vocabulary');
    trackReplace(/\bdiversification\b/g, "spreading investments", 'vocabulary');
    trackReplace(/\bRebalancing\b/g, "Adjusting", 'vocabulary');
    trackReplace(/\brebalancing\b/g, "adjusting", 'vocabulary');
    trackReplace(/\bBenchmark\b/g, "Comparison", 'vocabulary');
    trackReplace(/\bbenchmark\b/g, "comparison", 'vocabulary');
    trackReplace(/\bAllocation\b/g, "Division", 'vocabulary');
    trackReplace(/\ballocation\b/g, "division", 'vocabulary');
  }

  // ===========================================
  // TECHNICAL LEVEL ADJUSTMENTS (Advanced)
  // ===========================================
  else if (style.technicalLevel === 'advanced') {
    // CRITICAL FIX: Preserve capitalization by handling both cases
    trackReplace(/\bHow your investments are divided\b/g, "Portfolio allocation", 'vocabulary');
    trackReplace(/\bhow your investments are divided\b/g, "portfolio allocation", 'vocabulary');
    trackReplace(/\bInvestment mix\b/g, "Asset allocation", 'vocabulary');
    trackReplace(/\binvestment mix\b/g, "asset allocation", 'vocabulary');
    trackReplace(/\bMarket ups and downs\b/g, "Market volatility", 'vocabulary');
    trackReplace(/\bmarket ups and downs\b/g, "market volatility", 'vocabulary');
    trackReplace(/\bUps and downs\b/g, "Volatility", 'vocabulary');
    trackReplace(/\bups and downs\b/g, "volatility", 'vocabulary');
    trackReplace(/\bStocks\b/g, "Equities", 'vocabulary');
    trackReplace(/\bstocks\b/g, "equities", 'vocabulary');
    trackReplace(/\bGrowth\b/g, "Appreciation", 'vocabulary');
    trackReplace(/\bgrowth\b/g, "appreciation", 'vocabulary');
    trackReplace(/\bAvailable cash\b/g, "Liquidity", 'vocabulary');
    trackReplace(/\bavailable cash\b/g, "liquidity", 'vocabulary');
    trackReplace(/\bSpreading investments\b/g, "Diversification", 'vocabulary');
    trackReplace(/\bspreading investments\b/g, "diversification", 'vocabulary');
  }

  // Apply personal touch
  if (style.personalTouch === 'high' && style.commonPhrases?.length > 0) {
    const randomPhrase = style.commonPhrases[Math.floor(Math.random() * style.commonPhrases.length)];
    if (!styledContent.includes(randomPhrase)) {
      styledContent += `\n\nP.S. ${randomPhrase}`;
      transformations.push({ from: '', to: randomPhrase, type: 'personal' });
    }
  }

  // Apply signature elements if provided
  if (style.signatureGreeting) {
    trackReplace(/^Dear.*?,/m, style.signatureGreeting, 'signature');
  }
  if (style.signatureClosing) {
    trackReplace(/Best regards,?$/m, style.signatureClosing, 'signature');
  }

  // Return based on options
  if (options.returnMetadata) {
    return {
      content: styledContent,
      transformations,
      transformationCount: transformations.length,
      // Suggest AI regeneration if few transformations (< 10 changes)
      // This allows AI option even when some regex patterns match
      suggestAIRegeneration: transformations.length < 10
    };
  }

  return styledContent;
}

/**
 * Apply style using AI regeneration (comprehensive transformation)
 * Helper used by the facade — uses explicit DI rather than `this.aiService`
 * so the function is independently testable.
 *
 * @param {Object|null} aiService - Injected AI service (may be null)
 * @param {string} content - Original content
 * @param {Object} styleProfile - Style profile object
 * @param {Object} options - Optional settings
 * @returns {Promise<Object>} { content, tokensUsed, estimatedCost }
 */
export async function applyStyleWithAI(aiService, content, styleProfile, options = {}) {
  // Delegate to NewsletterAIService's regenerateWithStyle method
  if (!aiService) {
    throw new Error('AI Service not available. Call setAIService() first.');
  }

  return await aiService.regenerateWithStyle(content, styleProfile, options);
}
